import React, { useState, useEffect } from "react";
import { Loader2, Download, FileText, Pill, ClipboardPaste, X, ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const API = process.env.REACT_APP_BACKEND_URL;

// Parse a pharmacy label pasted from the OCP (or similar) directory. The
// input can be a labelled block (e.g. "Pharmacy Name: X / Address: Y /
// Phone: Z / Fax: W") or a bare address block copied from the college
// listing. We only extract:
//   • pharmacy_name (first sensible line or after "Name:" style label)
//   • pharmacy_address (street + city/province + postal, joined by ", ")
//   • pharmacy_phone
//   • pharmacy_fax
// Anything else in the pasted text is ignored.
const parsePharmacyLabel = (raw) => {
  if (!raw) return {};
  const text = raw.replace(/[\u200B-\u200D\uFEFF]/g, "");
  const out = {};

  const cleanPhone = (s) =>
    s
      .replace(/[^\d+\-()\s.]/g, "")
      // Strip leading +1 or 1 (country code) with optional separators
      .replace(/^\s*\+?\s*1\s*(?=[\s\-.(])/, "")
      .replace(/\s+/g, " ")
      // Ensure a single space after the closing parenthesis of the area code
      .replace(/\)\s*/, ") ")
      .trim();

  // Phone — prefer labelled matches. Try each label variant.
  const phoneMatch =
    text.match(/(?:Location\s+)?(?:Tele)?phone(?:\s*(?:Number|#))?\s*[:#]?\s*([+\d()\-.\s]{7,})/i) ||
    text.match(/\bTel\s*[:#]?\s*([+\d()\-.\s]{7,})/i);
  if (phoneMatch) out.pharmacyPhone = cleanPhone(phoneMatch[1]);

  const faxMatch =
    text.match(/(?:Location\s+)?Fax(?:\s*(?:Number|#))?\s*[:#]?\s*([+\d()\-.\s]{7,})/i);
  if (faxMatch) out.pharmacyFax = cleanPhone(faxMatch[1]);

  const postalRe = /\b([ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z]\s?\d[ABCEGHJ-NPRSTV-Z]\d)\b/i;

  // Address — look for a Canadian postal code (A1A 1A1). Handle two formats:
  //   1. "Toronto ON  M5J 1G7"   (city + postal on same line)
  //   2. "Windsor, ON" / "N9A 2W1" (postal on its own line, city above)
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  const looksLikeLabel = /^(Pharmacy\s+Name|Certificate|Accreditation|Manager|Category|Owner|Operator|General\s+Information|Community\s+pharmacy|Entitled)/i;

  const postalIdx = lines.findIndex((l) => postalRe.test(l));
  if (postalIdx >= 0) {
    const postalLine = lines[postalIdx]
      .replace(/^\s*(?:Location\s+)?Address\s*[:#]?\s*/i, "")
      .trim();
    const postalMatch = postalLine.match(postalRe);
    const postal = postalMatch ? postalMatch[1].toUpperCase().replace(/\s+/g, " ") : "";
    // Is the postal line just the postal code (with maybe minor punctuation)?
    const remainder = postalLine.replace(postalRe, "").replace(/[,\s]/g, "");
    const postalOnItsOwn = remainder.length < 3;

    let cityLine = "";
    let streetLine = "";
    if (postalOnItsOwn && postalIdx > 0) {
      cityLine = lines[postalIdx - 1]
        .replace(/^\s*(?:Location\s+)?Address\s*[:#]?\s*/i, "")
        .trim();
      streetLine =
        postalIdx > 1 ? lines[postalIdx - 2].trim() : "";
    } else {
      cityLine = postalLine.replace(postalRe, "").replace(/,\s*$/, "").trim();
      streetLine =
        postalIdx > 0
          ? lines[postalIdx - 1]
              .replace(/^\s*(?:Location\s+)?Address\s*[:#]?\s*/i, "")
              .trim()
          : "";
    }

    if (streetLine && looksLikeLabel.test(streetLine)) streetLine = "";

    out.pharmacyAddress = streetLine;
    if (postalOnItsOwn) {
      // Build "City, ON  Postal" style
      out.pharmacyCityPostal = cityLine
        ? `${cityLine.replace(/,\s*$/, "")}  ${postal}`.trim()
        : postal;
    } else {
      out.pharmacyCityPostal = postalLine.replace(/\s+/g, " ").trim();
    }
  } else {
    // Fallback: labelled "Address:" line
    const labelled = text.match(/(?:Location\s+)?Address\s*[:#]?\s*(.+)/i);
    if (labelled) out.pharmacyAddress = labelled[1].trim();
  }

  // Pharmacy name — labelled first
  const nameLabel = text.match(/Pharmacy\s+Name\s*[:#]?\s*(.+)/i);
  if (nameLabel) {
    out.pharmacyName = nameLabel[1].trim();
  } else {
    // Otherwise the first non-empty line that isn't boilerplate.
    const skipRe = /^(Certificate|Accreditation|Category|Manager|Owner|Operator|Phone|Tel|Telephone|Fax|Address|Location|Hours|Website|Email|General\s+Information|Community\s+pharmacy|Entitled)/i;
    for (const line of lines) {
      if (skipRe.test(line)) continue;
      if (postalRe.test(line)) continue;
      if (/^[\d()\-.\s+]+$/.test(line)) continue; // pure phone-shaped
      // Split concatenated boilerplate that follows the name.
      // "Haven PharmacyEntitled to operate" → "Haven Pharmacy"
      let cleaned = line
        .split(/Entitled\s+to\b/i)[0]
        .replace(/(Pharmacy|Drug\s*Mart|Drugs?|RX|Rexall|Shoppers|Guardian|IDA|PharmaChoice|Wellwise)Entitled.*$/i, "$1")
        .trim();
      if (!cleaned) continue;
      out.pharmacyName = cleaned;
      break;
    }
  }
  return out;
};

// Parse a patient label pasted from the PMS. Sample:
//   HCN: 9487630510  Sex: F
//   Knapaysweet, Anita
//   DOB: 1974-07-05
// Returns an object with any extracted fields (only present when found).
const parsePatientLabel = (raw) => {
  if (!raw) return {};
  // Strip zero-width chars often introduced by PMS copy/paste
  const text = raw.replace(/[\u200B-\u200D\uFEFF]/g, "");
  const out = {};

  // HCN may include an optional 2-letter OHIP version code after the digits
  // (e.g. "HCN: 9487630510 AB"). Some labels omit it — that's fine too.
  // Match the digits first, then look immediately after for a version code.
  const hcn = text.match(/HCN\s*[:#]?\s*([0-9][0-9\s-]{6,20})/i);
  if (hcn) {
    const digits = hcn[1].trim();
    const trailing = text
      .slice(hcn.index + hcn[0].length)
      .match(/^\s*([A-Za-z]{2})\b/);
    const vcode = trailing ? trailing[1].toUpperCase() : "";
    out.healthCard = vcode ? `${digits} ${vcode}` : digits;
  }

  const dob = text.match(
    /DOB\s*[:]?\s*(\d{4}[-/]\d{1,2}[-/]\d{1,2})/i
  );
  if (dob) {
    // Normalise to YYYY-MM-DD for the <input type="date">
    const [y, m, d] = dob[1].split(/[-/]/);
    out.dob = `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }

  // Look for a "Last, First" line — first non-empty line that contains a
  // comma, that isn't HCN/DOB/Phone/Sex/Address, and doesn't look like a date.
  const skipRe = /^(HCN|DOB|Sex|Phone|Address|Fax|Email)\b/i;
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  for (const line of lines) {
    if (skipRe.test(line)) continue;
    if (/^\d{4}[-/]\d{1,2}[-/]\d{1,2}/.test(line)) continue;
    const m = line.match(/^([^,]+),\s*(.+)$/);
    if (m) {
      out.lastName = m[1].trim();
      out.firstName = m[2].replace(/\s+/g, " ").trim();
      break;
    }
  }
  return out;
};

export default function Pharmacy() {
  const [meds, setMeds] = useState([]);
  const [labelText, setLabelText] = useState("");
  const [pharmacyLabelText, setPharmacyLabelText] = useState("");
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    dob: "",
    healthCard: "",
    medicationKey: "",
    pharmacyName: "",
    pharmacyAddress: "",
    pharmacyCityPostal: "",
    pharmacyPhone: "",
    pharmacyFax: "",
    // Page 1 additions
    pathway: "",             // "" | "A" | "B"
    verbalConsent: false,
    handoffDate: "",         // YYYY-MM-DD
    // Program fees on Page 2 — editable per customer, defaults 350 / 1050
    feeMonthly: "350",
    feeTotal: "1050",
  });
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pageImages, setPageImages] = useState([]);
  const [fileName, setFileName] = useState("hepcure-handoff.pdf");
  const [loading, setLoading] = useState(false);
  const [previewPage, setPreviewPage] = useState(1);
  const [pdfPassword, setPdfPassword] = useState("");
  // Server-side handoff token — used to trigger browser-native PDF downloads
  // via GET /api/pharmacy/handoff-download. This is the only reliable way to
  // deliver a correct filename across desktop, Android, and iOS Safari (which
  // ignores the `download` attribute on blob URLs).
  const [handoffToken, setHandoffToken] = useState("");

  useEffect(() => {
    fetch(`${API}/api/pharmacy/medications`)
      .then((r) => r.json())
      .then((d) => setMeds(d))
      .catch(() =>
        toast.error("Couldn't load medication list — please refresh.")
      );
  }, []);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const applyLabel = (text) => {
    setLabelText(text);
    const parsed = parsePatientLabel(text);
    if (Object.keys(parsed).length === 0) return;
    setForm((f) => ({
      ...f,
      firstName: parsed.firstName ?? f.firstName,
      lastName: parsed.lastName ?? f.lastName,
      dob: parsed.dob ?? f.dob,
      healthCard: parsed.healthCard ?? f.healthCard,
    }));
    const fields = [];
    if (parsed.firstName || parsed.lastName) fields.push("name");
    if (parsed.dob) fields.push("date of birth");
    if (parsed.healthCard) fields.push("health card");
    if (fields.length) {
      toast.success("Patient label parsed", {
        description: `Auto-filled: ${fields.join(", ")}.`,
      });
    }
  };

  const applyPharmacyLabel = (text) => {
    setPharmacyLabelText(text);
    const parsed = parsePharmacyLabel(text);
    if (Object.keys(parsed).length === 0) return;
    setForm((f) => ({
      ...f,
      pharmacyName: parsed.pharmacyName ?? f.pharmacyName,
      pharmacyAddress: parsed.pharmacyAddress ?? f.pharmacyAddress,
      pharmacyCityPostal: parsed.pharmacyCityPostal ?? f.pharmacyCityPostal,
      pharmacyPhone: parsed.pharmacyPhone ?? f.pharmacyPhone,
      pharmacyFax: parsed.pharmacyFax ?? f.pharmacyFax,
    }));
    const fields = [];
    if (parsed.pharmacyName) fields.push("name");
    if (parsed.pharmacyAddress || parsed.pharmacyCityPostal) fields.push("address");
    if (parsed.pharmacyPhone) fields.push("phone");
    if (parsed.pharmacyFax) fields.push("fax");
    if (fields.length) {
      toast.success("Pharmacy label parsed", {
        description: `Auto-filled: ${fields.join(", ")}.`,
      });
    }
  };

  const clearPharmacyLabel = () => {
    setPharmacyLabelText("");
    setForm((f) => ({
      ...f,
      pharmacyName: "",
      pharmacyAddress: "",
      pharmacyCityPostal: "",
      pharmacyPhone: "",
      pharmacyFax: "",
    }));
    toast.info("Pharmacy fields cleared");
  };

  const clearAll = () => {
    setLabelText("");
    setPharmacyLabelText("");
    setForm({
      firstName: "",
      lastName: "",
      dob: "",
      healthCard: "",
      medicationKey: "",
      pharmacyName: "",
      pharmacyAddress: "",
      pharmacyCityPostal: "",
      pharmacyPhone: "",
      pharmacyFax: "",
      pathway: "",
      verbalConsent: false,
      handoffDate: "",
      feeMonthly: "350",
      feeTotal: "1050",
    });
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    setFileName("hepcure-handoff.pdf");
    setHandoffToken("");
    setPdfPassword("");
    toast.info("Cleared", { description: "Ready for the next patient." });
  };

  const pasteFromClipboard = async () => {
    // Try 1: Programmatic paste via focused textarea + execCommand.
    // This bypasses the browser permission dialog on Chrome/Firefox desktop
    // and on Android WebView. iOS Safari always blocks this.
    const textarea = document.getElementById("patientLabel");
    if (textarea) {
      textarea.focus();
      try {
        const ok = document.execCommand("paste");
        // After execCommand, the textarea value may be updated synchronously.
        if (ok && textarea.value && textarea.value !== labelText) {
          applyLabel(textarea.value);
          return;
        }
      } catch (_) {
        /* ignore, fall through */
      }
    }

    // Try 2: Async Clipboard API.
    // Note: on iOS Safari 16+, this triggers Apple's mandatory "Paste"
    // consent tooltip — that popup is a native OS security guardrail
    // enforced by WebKit and cannot be bypassed by any web app.
    try {
      const text = await navigator.clipboard.readText();
      if (!text) {
        toast.error("Clipboard is empty");
        return;
      }
      applyLabel(text);
    } catch (err) {
      toast.error("Couldn't read clipboard", {
        description:
          "On iOS, tap the textarea and use your keyboard's Paste option.",
      });
    }
  };

  const hasPatientInfo =
    form.firstName.trim() &&
    form.lastName.trim() &&
    form.dob &&
    form.healthCard.trim() &&
    form.medicationKey;

  const hasPharmacyInfo =
    form.pharmacyName.trim() ||
    form.pharmacyAddress.trim() ||
    form.pharmacyCityPostal.trim() ||
    form.pharmacyPhone.trim() ||
    form.pharmacyFax.trim();

  const canSubmit = hasPatientInfo || hasPharmacyInfo;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    setPageImages([]);
    setHandoffToken("");

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 90000);
    const requestBody = JSON.stringify({
      first_name: form.firstName,
      last_name: form.lastName,
      dob: form.dob,
      health_card: form.healthCard,
      medication_key: form.medicationKey,
      pharmacy_name: form.pharmacyName,
      pharmacy_address: form.pharmacyAddress,
      pharmacy_city_postal: form.pharmacyCityPostal,
      pharmacy_tel: form.pharmacyPhone,
      pharmacy_fax: form.pharmacyFax,
      pathway: form.pathway || null,
      verbal_consent: !!form.verbalConsent,
      handoff_date: form.handoffDate || null,
      fee_monthly: form.feeMonthly ? Number(form.feeMonthly) : null,
      fee_total: form.feeTotal ? Number(form.feeTotal) : null,
    });

    try {
      // Fetch the full PDF (for download) and preview page images in parallel
      const [pdfRes, previewRes] = await Promise.all([
        fetch(`${API}/api/pharmacy/handoff-pdf`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          signal: controller.signal,
          body: requestBody,
        }),
        fetch(`${API}/api/pharmacy/handoff-preview`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          signal: controller.signal,
          body: requestBody,
        }),
      ]);

      if (!pdfRes.ok) {
        const msg = (await pdfRes.text()).slice(0, 200);
        throw new Error(`Server returned ${pdfRes.status}. ${msg}`);
      }
      if (!previewRes.ok) {
        const msg = (await previewRes.text()).slice(0, 200);
        throw new Error(`Preview server returned ${previewRes.status}. ${msg}`);
      }

      const blob = await pdfRes.blob();
      if (blob.size < 1000) {
        throw new Error(`Server returned empty PDF (${blob.size} bytes).`);
      }
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
      // Capture the encryption password so the operator can share it verbally
      // or in a separate email to the receiving pharmacy.
      const pwd = pdfRes.headers.get("X-Pdf-Password") || "";
      setPdfPassword(pwd);
      // Capture the server-side handoff token. Subsequent download buttons
      // use a GET request against this token so the browser handles the
      // download natively — this is essential for iOS Safari + Android
      // Chrome where blob `download` attributes are ignored or truncated.
      const token = pdfRes.headers.get("X-Handoff-Token") || "";
      setHandoffToken(token);

      const preview = await previewRes.json();
      setPageImages(preview.page_images || []);
      setPreviewPage(1);

      const safeName = `${form.firstName} ${form.lastName}`
        .replace(/[^a-zA-Z0-9 _-]/g, "")
        .trim() || "Patient";
      setFileName(`HepCURE Registration - ${safeName}.pdf`);
      toast.success("Handoff PDF generated", {
        description: "Preview below — then click Download to save.",
      });
    } catch (err) {
      let description = "Please check the details and try again.";
      if (err.name === "AbortError") {
        description =
          "Request timed out after 90 seconds. Server may be busy — please retry.";
      } else if (err.message) {
        description = err.message.slice(0, 220);
      }
      toast.error("Could not generate the PDF", { description });
      // eslint-disable-next-line no-console
      console.error("PDF generation error:", err);
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  };

  // Trigger the browser to download the handoff PDF via a GET request
  // against the server-side token. Using a real navigation (not fetch+blob)
  // lets the browser honor the backend's `Content-Disposition` header —
  // this is the only approach that produces the correct filename on iOS
  // Safari, Android Chrome, and desktop simultaneously.
  //
  // We include the desired filename as the LAST PATH SEGMENT of the URL
  // because iOS Safari opens PDFs in its built-in viewer (rather than
  // downloading them), and when the user hits Share → Save to Files, iOS
  // uses the URL's last path segment as the suggested filename — ignoring
  // Content-Disposition entirely. Desktop browsers get the correct name
  // via Content-Disposition; iOS gets it via the URL. Belt and suspenders.
  const buildDownloadFilename = (which) => {
    const safeName = `${form.firstName} ${form.lastName}`
      .replace(/[^a-zA-Z0-9 _-]/g, "")
      .trim() || "Patient";
    const suffix = which === "all" ? "" : ` (Page ${which})`;
    return `HepCURE Registration - ${safeName}${suffix}.pdf`;
  };

  const downloadPdfForPages = (which) => {
    if (!handoffToken) {
      toast.error("Please generate the PDF first.");
      return;
    }
    const fname = buildDownloadFilename(which);
    const url =
      `${API}/api/pharmacy/handoff-download/${encodeURIComponent(fname)}` +
      `?token=${encodeURIComponent(handoffToken)}` +
      `&pages=${encodeURIComponent(which)}`;

    // Hidden anchor click — works on every browser (unlike window.open which
    // can be blocked as a popup on iOS when the URL is served as an
    // attachment). We also set the `download` attribute to the real
    // filename as a defensive fallback for browsers that honor it.
    const a = document.createElement("a");
    a.href = url;
    a.rel = "noopener";
    a.setAttribute("download", fname);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div
      className="min-h-screen bg-brand-offwhite pt-24 sm:pt-28 pb-10 sm:pb-16 overflow-x-hidden"
      data-testid="pharmacy-page"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <span className="inline-flex items-center gap-2 rounded-full bg-brand-teal/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-brand-teal-dark">
            <Pill className="h-3.5 w-3.5" strokeWidth={2} />
            Internal · Partner-pharmacy tool
          </span>
          <h1 className="mt-4 text-3xl sm:text-4xl font-bold font-heading text-brand-navy leading-tight">
            Generate a partner-pharmacy handoff PDF
          </h1>
          <p className="mt-3 text-slate-600 max-w-2xl">
            Fill in the patient details and choose the prescribed medication.
            A pre-filled handoff sheet will render below — preview it, then
            download.
          </p>
        </div>

        <div className="grid lg:grid-cols-12 gap-8 items-stretch max-w-full">
          {/* Left — Form */}
          <form
            onSubmit={onSubmit}
            data-testid="pharmacy-form"
            className="lg:col-span-6 min-w-0 max-w-full flex flex-col rounded-2xl border border-gray-100 bg-white p-6 sm:p-8 shadow-sm space-y-5"
          >
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <Label
                  htmlFor="patientLabel"
                  className="text-brand-navy flex items-center gap-2"
                >
                  <ClipboardPaste className="h-4 w-4 text-brand-teal" />
                  Patient label
                </Label>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={pasteFromClipboard}
                    data-testid="pharmacy-paste-btn"
                    className="inline-flex items-center gap-1.5 rounded-full bg-brand-teal hover:bg-brand-teal-dark text-white text-xs font-semibold px-3 py-1.5 transition-colors whitespace-nowrap"
                  >
                    <ClipboardPaste className="h-3.5 w-3.5" />
                    Paste
                  </button>
                  <button
                    type="button"
                    onClick={clearAll}
                    data-testid="pharmacy-clear"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-brand-navy transition-colors whitespace-nowrap"
                  >
                    <X className="h-3.5 w-3.5" />
                    Clear
                  </button>
                </div>
              </div>
              <Textarea
                id="patientLabel"
                data-testid="pharmacy-label-paste"
                value={labelText}
                onChange={(e) => applyLabel(e.target.value)}
                onPaste={(e) => {
                  const t = e.clipboardData.getData("text");
                  if (t) {
                    e.preventDefault();
                    applyLabel(t);
                  }
                }}
                rows={7}
                className="text-base md:text-xs font-mono resize-none"
              />
            </div>

            <div className="border-t border-gray-100 pt-5"></div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName" className="text-brand-navy">
                  First name
                </Label>
                <Input
                  id="firstName"
                  data-testid="pharmacy-first-name"
                  value={form.firstName}
                  onChange={update("firstName")}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName" className="text-brand-navy">
                  Last name
                </Label>
                <Input
                  id="lastName"
                  data-testid="pharmacy-last-name"
                  value={form.lastName}
                  onChange={update("lastName")}
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="dob" className="text-brand-navy">
                  Date of birth
                </Label>
                <Input
                  id="dob"
                  type="text"
                  inputMode="numeric"
                  data-testid="pharmacy-dob"
                  value={form.dob}
                  onChange={update("dob")}
                  pattern="\d{4}-\d{2}-\d{2}"
                  title="Format: YYYY-MM-DD"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="healthCard" className="text-brand-navy">
                  Health card #
                </Label>
                <Input
                  id="healthCard"
                  data-testid="pharmacy-health-card"
                  value={form.healthCard}
                  onChange={update("healthCard")}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-brand-navy">Prescribed medication</Label>
              <Select
                value={form.medicationKey}
                onValueChange={(v) =>
                  setForm((f) => ({ ...f, medicationKey: v }))
                }
              >
                <SelectTrigger data-testid="pharmacy-medication" className="min-w-0">
                  <SelectValue placeholder="Select medication" className="truncate" />
                </SelectTrigger>
                <SelectContent className="max-w-[calc(100vw-3rem)] w-[var(--radix-select-trigger-width)]">
                  {meds.map((m) => (
                    <SelectItem
                      key={m.key}
                      value={m.key}
                      data-testid={`pharmacy-med-${m.key}`}
                      className="truncate"
                    >
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.medicationKey && (
                <p className="text-xs text-slate-500 mt-1">
                  {
                    meds.find((m) => m.key === form.medicationKey)?.posology
                  }
                </p>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <Label
                  htmlFor="pharmacyLabel"
                  className="text-brand-navy flex items-center gap-2"
                >
                  <ClipboardPaste className="h-4 w-4 text-brand-teal" />
                  Pharmacy label
                </Label>
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const text = await navigator.clipboard.readText();
                        if (text) applyPharmacyLabel(text);
                        else toast.error("Clipboard is empty");
                      } catch {
                        toast.error("Couldn't read clipboard", {
                          description: "Tap the textarea and use Paste.",
                        });
                      }
                    }}
                    data-testid="pharmacy-label-paste-btn"
                    className="inline-flex items-center gap-1.5 rounded-full bg-brand-teal hover:bg-brand-teal-dark text-white text-xs font-semibold px-3 py-1.5 transition-colors whitespace-nowrap"
                  >
                    <ClipboardPaste className="h-3.5 w-3.5" />
                    Paste
                  </button>
                  <button
                    type="button"
                    onClick={clearPharmacyLabel}
                    data-testid="pharmacy-label-clear"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-brand-navy transition-colors whitespace-nowrap"
                  >
                    <X className="h-3.5 w-3.5" />
                    Clear
                  </button>
                </div>
              </div>
              <Textarea
                id="pharmacyLabel"
                data-testid="pharmacy-label-textarea"
                value={pharmacyLabelText}
                onChange={(e) => applyPharmacyLabel(e.target.value)}
                onPaste={(e) => {
                  const t = e.clipboardData.getData("text");
                  if (t) {
                    e.preventDefault();
                    applyPharmacyLabel(t);
                  }
                }}
                rows={7}
                className="text-base md:text-xs font-mono resize-none"
              />
              <a
                href="https://members.ocpinfo.com/tcpr/public/pr/en/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="ocp-lookup-link"
                className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-brand-teal hover:text-brand-teal-dark hover:underline"
              >
                Look up pharmacy on OCP →
              </a>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="pharmacyName" className="text-brand-navy">
                  Pharmacy name
                </Label>
                <Input
                  id="pharmacyName"
                  data-testid="pharmacy-name"
                  value={form.pharmacyName}
                  onChange={update("pharmacyName")}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pharmacyAddress" className="text-brand-navy">
                  Street
                </Label>
                <Input
                  id="pharmacyAddress"
                  data-testid="pharmacy-address"
                  value={form.pharmacyAddress}
                  onChange={update("pharmacyAddress")}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="pharmacyCityPostal" className="text-brand-navy">
                  City / postal
                </Label>
                <Input
                  id="pharmacyCityPostal"
                  data-testid="pharmacy-city-postal"
                  value={form.pharmacyCityPostal}
                  onChange={update("pharmacyCityPostal")}
                />
              </div>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="pharmacyPhone" className="text-brand-navy">
                    Telephone
                  </Label>
                  <Input
                    id="pharmacyPhone"
                    data-testid="pharmacy-tel"
                    value={form.pharmacyPhone}
                    onChange={update("pharmacyPhone")}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pharmacyFax" className="text-brand-navy">
                    Fax
                  </Label>
                  <Input
                    id="pharmacyFax"
                    data-testid="pharmacy-fax"
                    value={form.pharmacyFax}
                    onChange={update("pharmacyFax")}
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-gray-100 pt-4 space-y-3 min-w-0">
              <div className="space-y-1">
                <Label className="text-brand-navy">Program fees</Label>
                <p className="text-xs text-gray-500">
                  Shown in the invoice section of the PDF — adjust per customer if needed.
                </p>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="feeMonthly" className="text-brand-navy">
                    Monthly amount ($)
                  </Label>
                  <Input
                    id="feeMonthly"
                    data-testid="pharmacy-fee-monthly"
                    type="number" min="0" step="1" inputMode="numeric"
                    value={form.feeMonthly}
                    onChange={update("feeMonthly")}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="feeTotal" className="text-brand-navy">
                    Full treatment amount ($)
                  </Label>
                  <Input
                    id="feeTotal"
                    data-testid="pharmacy-fee-total"
                    type="number" min="0" step="1" inputMode="numeric"
                    value={form.feeTotal}
                    onChange={update("feeTotal")}
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-gray-100 pt-4 space-y-4 min-w-0">
              <div className="space-y-2">
                <Label className="text-brand-navy">Delivery pathway</Label>
                <div className="grid grid-cols-2 gap-2">
                  {["A", "B"].map((v) => (
                    <button
                      key={v}
                      type="button"
                      data-testid={`pathway-${v}`}
                      onClick={() => setForm((f) => ({ ...f, pathway: f.pathway === v ? "" : v }))}
                      className={`px-3 py-2 rounded-lg border text-sm font-semibold transition text-left ${
                        form.pathway === v
                          ? "bg-brand-navy text-white border-brand-navy"
                          : "bg-white text-brand-navy border-gray-200 hover:border-brand-teal"
                      }`}
                    >
                      <span className="font-black mr-2">{v}</span>
                      {v === "A" ? "Established" : "New"}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4 min-w-0">
                <div className="space-y-2 min-w-0">
                  <Label className="text-brand-navy">Consent</Label>
                  <label className="flex items-start gap-2 cursor-pointer select-none pt-1.5">
                    <input
                      type="checkbox"
                      data-testid="verbal-consent"
                      checked={form.verbalConsent}
                      onChange={(e) => setForm((f) => ({ ...f, verbalConsent: e.target.checked }))}
                      className="mt-0.5 h-4 w-4 accent-brand-navy"
                    />
                    <span className="text-sm text-brand-navy leading-snug">
                      Verbal consent obtained
                      <span className="block text-[10px] text-slate-500 mt-0.5">Prints a check on page 1.</span>
                    </span>
                  </label>
                </div>
                <div className="space-y-2 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <Label htmlFor="handoffDate" className="text-brand-navy">
                      Handoff date
                    </Label>
                    {form.handoffDate && (
                      <button
                        type="button"
                        data-testid="clear-handoff-date"
                        onClick={() => setForm((f) => ({ ...f, handoffDate: "" }))}
                        className="text-xs text-slate-500 hover:text-brand-navy underline"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                  <Input
                    id="handoffDate"
                    data-testid="handoff-date"
                    type="date"
                    value={form.handoffDate}
                    onChange={update("handoffDate")}
                    style={{ WebkitAppearance: "none", minWidth: 0 }}
                    className="w-full max-w-full block box-border"
                  />
                </div>
              </div>
            </div>

            <Button
              type="submit"
              disabled={!canSubmit || loading}
              data-testid="pharmacy-submit"
              className="w-full mt-auto bg-brand-teal hover:bg-brand-teal-dark text-white"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating PDF…
                </>
              ) : (
                <>
                  <FileText className="h-4 w-4 mr-2" />
                  Generate handoff PDF
                </>
              )}
            </Button>
          </form>

          {/* Right — Preview */}
          <div className="lg:col-span-6 min-w-0 max-w-full">
            {pdfUrl ? (
              <div className="rounded-2xl border border-gray-100 bg-white overflow-hidden shadow-sm">
                <div className="flex items-center justify-between p-4 border-b border-gray-100 flex-wrap gap-3">
                  <div>
                    <p className="text-sm font-semibold text-brand-navy">
                      Document Preview
                    </p>
                    {pdfPassword && (
                      <p className="text-xs text-slate-500 mt-0.5">
                        Password-protected PDF
                      </p>
                    )}
                  </div>
                  <a
                    href={
                      handoffToken
                        ? `${API}/api/pharmacy/handoff-download/${encodeURIComponent(
                            buildDownloadFilename("all")
                          )}?token=${encodeURIComponent(
                            handoffToken
                          )}&pages=all&disposition=inline`
                        : pdfUrl
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="pharmacy-open-tab"
                    className="inline-flex items-center gap-1.5 rounded-full border border-brand-teal text-brand-teal hover:bg-brand-teal/10 text-sm font-semibold px-3 py-2 transition whitespace-nowrap"
                  >
                    <Maximize2 className="h-4 w-4" />
                    View Full Screen
                  </a>
                </div>

                {/* Password callout — shown to the HepCURE operator so they know the password to communicate to the pharmacy */}
                {pdfPassword && (
                  <div
                    data-testid="pharmacy-password-callout"
                    className="mx-4 mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-start gap-3"
                  >
                    <div className="mt-0.5 flex-shrink-0 h-8 w-8 rounded-full bg-amber-100 flex items-center justify-center">
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#b45309"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="h-4 w-4"
                      >
                        <rect x="3" y="11" width="18" height="11" rx="2" />
                        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                      </svg>
                    </div>
                    <div className="text-sm text-amber-900">
                      <p className="font-semibold">
                        PDF password:{" "}
                        <span
                          className="ml-1 rounded bg-white px-2 py-0.5 font-mono tracking-widest border border-amber-200"
                          data-testid="pharmacy-password-value"
                        >
                          {pdfPassword}
                        </span>
                      </p>
                      <p className="mt-1 text-xs leading-snug text-amber-800">
                        Format: patient&apos;s first initial + last initial + birth
                        month (MM) + last 2 digits of Health Card. Share this
                        format with the pharmacy so they can open the file.
                      </p>
                    </div>
                  </div>
                )}

                {/* Desktop — page image preview (one page at a time) */}
                <div className="hidden md:block">
                  <div className="p-4 bg-slate-50">
                    <div className="border border-slate-200 rounded-xl bg-white overflow-hidden">
                      {pageImages[previewPage - 1] ? (
                        <img
                          src={pageImages[previewPage - 1]}
                          alt={`Page ${previewPage} preview`}
                          data-testid="pharmacy-preview-image"
                          className="block w-full h-auto"
                        />
                      ) : (
                        <div className="text-sm text-slate-400 p-8 text-center">
                          Rendering preview…
                        </div>
                      )}
                    </div>

                    {/* Page navigation */}
                    <div className="mt-4 flex items-center justify-center gap-4 rounded-2xl bg-white border border-slate-200 py-3 px-6">
                      <button
                        type="button"
                        onClick={() => setPreviewPage((p) => Math.max(1, p - 1))}
                        disabled={previewPage === 1}
                        data-testid="pharmacy-preview-prev"
                        className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2 bg-slate-100 hover:bg-slate-200 text-brand-navy font-semibold text-sm transition disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <ChevronLeft className="h-4 w-4" />
                        Prev
                      </button>

                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <span className="font-semibold">Page</span>
                        <input
                          type="number"
                          min={1}
                          max={pageImages.length || 1}
                          value={previewPage}
                          onChange={(e) => {
                            const v = parseInt(e.target.value, 10);
                            const max = pageImages.length || 1;
                            if (!isNaN(v) && v >= 1 && v <= max)
                              setPreviewPage(v);
                          }}
                          data-testid="pharmacy-preview-page-input"
                          className="w-14 h-9 text-center rounded-md border border-slate-200 text-brand-navy font-bold"
                        />
                        <span className="font-semibold">
                          of {pageImages.length || 1}
                        </span>
                      </div>

                      <button
                        type="button"
                        onClick={() =>
                          setPreviewPage((p) =>
                            Math.min(pageImages.length || 1, p + 1)
                          )
                        }
                        disabled={previewPage === (pageImages.length || 1)}
                        data-testid="pharmacy-preview-next"
                        className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2 bg-slate-100 hover:bg-slate-200 text-brand-navy font-semibold text-sm transition disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        Next
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>

                    {/* Download options */}
                    <div className="mt-3 flex flex-wrap items-center justify-center gap-2">
                      <span className="text-xs font-semibold uppercase tracking-wider text-slate-500 mr-1">
                        Download:
                      </span>
                      <button
                        type="button"
                        onClick={() => downloadPdfForPages("all")}
                        data-testid="pharmacy-download-all"
                        className="inline-flex items-center gap-1.5 rounded-lg bg-brand-teal hover:bg-brand-teal-dark text-white font-semibold text-sm px-4 py-2 transition"
                      >
                        <Download className="h-4 w-4" />
                        Download PDF
                      </button>
                    </div>
                  </div>
                </div>

                {/* Mobile — image preview + download options */}
                <div className="md:hidden p-4 bg-slate-50">
                  <div className="border-2 border-dashed border-slate-200 rounded-xl bg-white overflow-hidden">
                    {pageImages[previewPage - 1] && (
                      <img
                        src={pageImages[previewPage - 1]}
                        alt={`Page ${previewPage}`}
                        className="w-full h-auto"
                      />
                    )}
                  </div>
                  <div className="mt-3 flex items-center justify-center gap-3">
                    <button
                      type="button"
                      onClick={() => setPreviewPage((p) => Math.max(1, p - 1))}
                      disabled={previewPage === 1}
                      className="rounded-lg px-3 py-2 bg-white border border-slate-200 text-brand-navy font-semibold text-sm disabled:opacity-40"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>
                    <span className="text-sm text-slate-600 font-semibold">
                      Page {previewPage} of {pageImages.length || 1}
                    </span>
                    <button
                      type="button"
                      onClick={() =>
                        setPreviewPage((p) =>
                          Math.min(pageImages.length || 1, p + 1)
                        )
                      }
                      disabled={previewPage === (pageImages.length || 1)}
                      className="rounded-lg px-3 py-2 bg-white border border-slate-200 text-brand-navy font-semibold text-sm disabled:opacity-40"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={() => downloadPdfForPages("all")}
                      data-testid="pharmacy-download-all-mobile"
                      className="w-full inline-flex items-center justify-center gap-1 rounded-lg bg-brand-teal text-white font-semibold text-xs px-2 py-2"
                    >
                      Download PDF
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-10 text-center text-slate-500">
                <FileText className="mx-auto h-8 w-8 text-slate-300" />
                <p className="mt-3 text-sm">
                  Fill in the form and click{" "}
                  <b className="text-brand-navy">Generate handoff PDF</b> to
                  preview it here.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
